import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  AronAdminConversation,
  AronAdminConversationDetail,
  AronAdminOwner,
} from "../types";

const PAGE_SIZE = 100;

export interface AdminConversationsParams {
  search?: string;
  provider?: string; // "claude" | "deepseek"
  user?: string;
  ordering?: string; // "activity" | "cost" | "user" | "messages"
  dir?: "asc" | "desc";
}

interface AdminConversationsPage {
  rows: AronAdminConversation[];
  count: number;
  hasNext: boolean;
}

// CEO+DEV only — list ALL users' conversations (cross-user audit). Each call is logged
// server-side (AronAdminAccessLog). Filtering and sorting are the SERVER's job: doing
// them here would only ever narrow the first page, so a colleague's older thread would
// drop out of the audit as soon as the list outgrows one page. Paging is "load more" —
// `count` is the true total across every page.
export function useAdminConversations(params: AdminConversationsParams) {
  return useInfiniteQuery({
    queryKey: ["aron", "admin", "conversations", params],
    initialPageParam: 1,
    getNextPageParam: (last: AdminConversationsPage, all) =>
      last.hasNext ? all.length + 1 : undefined,
    staleTime: 30_000,
    queryFn: async ({ pageParam }): Promise<AdminConversationsPage> => {
      const qs = new URLSearchParams({
        page_size: String(PAGE_SIZE),
        page: String(pageParam),
      });
      if (params.search) qs.set("search", params.search);
      if (params.provider) qs.set("provider", params.provider);
      if (params.user) qs.set("user", params.user);
      if (params.ordering) qs.set("ordering", params.ordering);
      if (params.dir) qs.set("dir", params.dir);
      const r = await apiClient.get<{
        data: AronAdminConversation[];
        meta: { count?: number; next?: string | null };
      }>(`/aron/admin/conversations/?${qs.toString()}`);
      const rows = r.data.data ?? [];
      return {
        rows,
        count: r.data.meta?.count ?? rows.length,
        hasNext: !!r.data.meta?.next,
      };
    },
  });
}

// Everyone who owns at least one ARON thread — the people filter. Read from the whole
// table so nobody is missing from the dropdown just because their threads sit on page 3.
export function useAdminConversationOwners() {
  return useQuery<AronAdminOwner[]>({
    queryKey: ["aron", "admin", "conversation-owners"],
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronAdminOwner[] }>(
        "/aron/admin/conversations/owners/",
      );
      return r.data.data ?? [];
    },
  });
}

// Full transcript of one conversation (any user). Viewing is itself audited server-side.
export function useAdminConversation(id: string | null) {
  return useQuery<AronAdminConversationDetail>({
    queryKey: ["aron", "admin", "conversation", id],
    enabled: !!id,
    // Ten sam transkrypt bywa czytany przez dwa widoki naraz (panel podglądu w
    // ARON-ie i pasek kart). Bez tego każdy nowy obserwator dokłada żądanie —
    // a każde żądanie to osobny wpis w rejestrze dostępów (RODO).
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronAdminConversationDetail }>(
        `/aron/admin/conversations/${id}/`,
      );
      return r.data.data;
    },
  });
}
